import React, { useEffect, useRef, useState } from "react";
import { api, API, getToken } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import {
  FolderUp, UploadCloud, Loader2, Sparkles, CheckCircle2, AlertTriangle, FileText, Trash2, Save, Wand2,
} from "lucide-react";
import { toast } from "sonner";

// Bulk multi-file AI CTA importer.
// Flow:
//   1. User picks N files (drag-drop or file picker)
//   2. POST /api/ctas/bulk-extract returns {job_id, total}
//   3. Poll GET /api/ctas/bulk-jobs/{id} every 1.5s until status==='done'
//   4. Show table: filename, study, sponsor, # visits/activities/invoicables, coverage, site dropdown
//   5. User assigns site per row (or bulk-assigns via "Apply site to all"), unchecks rows to skip
//   6. POST /api/ctas/bulk-save persists checked rows

const POLL_MS = 1500;
const MAX_FILES = 25;

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString()}`;

const bandColor = (band) => ({
  excellent: "bg-emerald-100 text-emerald-800 border-emerald-200",
  good: "bg-blue-100 text-blue-800 border-blue-200",
  fair: "bg-amber-100 text-amber-800 border-amber-200",
  poor: "bg-red-100 text-red-800 border-red-200",
}[band] || "bg-slate-100 text-slate-700 border-slate-200");

export default function BulkCTAImport({ sites, onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("pick"); // pick | extracting | review | saving
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]); // [{ filename, data, coverage, error, site_id, include }]
  const [bulkSite, setBulkSite] = useState("");
  const [saveResult, setSaveResult] = useState(null);
  const inputRef = useRef();
  const pollRef = useRef();

  const reset = () => {
    setStep("pick"); setJob(null); setRows([]); setBulkSite(""); setSaveResult(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (files.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files per batch — you picked ${files.length}`);
      return;
    }
    setStep("extracting");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const r = await fetch(`${API}/ctas/bulk-extract`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Bulk extract failed");
      setJob({ id: data.job_id, total: data.total, completed: 0, status: "running", results: [] });
      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const j = await api.get(`/ctas/bulk-jobs/${data.job_id}`).then((res) => res.data);
          setJob(j);
          if (j.status === "done" || j.status === "error") {
            clearInterval(pollRef.current); pollRef.current = null;
            // Build review rows. Stable `_k` survives removeRow shifts so
            // React keeps row state (Checkbox / Site dropdown) aligned to the
            // correct row instead of the array index.
            const mkKey = () => Math.random().toString(36).slice(2);
            const successful = (j.results || [])
              .filter((res) => res.status === "done" && res.data)
              .map((res) => ({
                _k: mkKey(),
                filename: res.filename,
                data: res.data,
                coverage: res.coverage,
                error: null,
                site_id: "",
                include: true,
              }));
            const failed = (j.results || [])
              .filter((res) => res.status === "error")
              .map((res) => ({ _k: mkKey(), filename: res.filename, data: null, coverage: null,
                                error: res.error, site_id: "", include: false }));
            setRows([...successful, ...failed]);
            setStep("review");
            if (failed.length > 0) toast.error(`${failed.length} file(s) failed extraction — see review`);
            if (successful.length > 0) toast.success(`Extracted ${successful.length} CTA(s) — review and save`);
          }
        } catch (err) {
          clearInterval(pollRef.current); pollRef.current = null;
          toast.error(err.message);
          setStep("pick");
        }
      }, POLL_MS);
    } catch (err) {
      toast.error(err.message);
      setStep("pick");
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (step !== "pick") return;
    handleFiles(e.dataTransfer?.files);
  };
  const onPick = (e) => handleFiles(e.target.files);

  const updateRow = (idx, patch) => setRows((p) => p.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const applyBulkSite = () => {
    if (!bulkSite) return;
    setRows((p) => p.map((r) => r.error ? r : { ...r, site_id: bulkSite }));
    toast.success("Site applied to all extracted rows");
  };
  const removeRow = (idx) => setRows((p) => p.filter((_, i) => i !== idx));

  const autoAssignBySponsor = async () => {
    try {
      const r = await api.get("/ctas/sponsor-site-map");
      const map = r.data?.map || {};
      let matched = 0, ambiguous = 0;
      setRows((prev) => prev.map((row) => {
        if (row.error || row.site_id) return row; // skip rows that are errored or already assigned
        const sp = (row.data?.sponsor || "").trim().toLowerCase();
        const hit = sp && map[sp];
        if (hit) {
          matched += 1;
          if (hit.confidence !== "high") ambiguous += 1;
          return { ...row, site_id: hit.site_id };
        }
        return row;
      }));
      if (matched === 0) {
        toast.info("No sponsors matched existing CTAs — assign sites manually");
      } else if (ambiguous > 0) {
        toast.success(`Auto-assigned ${matched} row(s); ${ambiguous} sponsor(s) had ambiguous mapping — please verify.`);
      } else {
        toast.success(`Auto-assigned ${matched} row(s) by sponsor match`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const includedRows = rows.filter((r) => r.include && !r.error);
  const allHaveSite = includedRows.length > 0 && includedRows.every((r) => r.site_id);

  const saveAll = async () => {
    if (!allHaveSite) {
      toast.error("Assign a site to every selected row before saving");
      return;
    }
    setStep("saving");
    try {
      const items = includedRows.map((r) => ({
        source_filename: r.filename,
        cta: {
          ...r.data,
          site_id: r.site_id,
          // Drop any stray internal fields the backend doesn't expect
          _counts: undefined,
        },
      }));
      const res = await api.post("/ctas/bulk-save", { items });
      setSaveResult(res.data);
      const created = res.data.total_created;
      const errs = res.data.total_errors;
      if (created > 0) toast.success(`Saved ${created} CTA${created !== 1 ? "s" : ""}`);
      if (errs > 0) toast.error(`${errs} row(s) failed to save — see details`);
      onCreated?.();
      // Stay in review step but in a "saved" sub-state
      setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setStep("review");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-ai-import-btn">
          <FolderUp className="w-4 h-4 mr-1" style={{ color: "#0A4C95" }} /> Bulk AI Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{ color: "#F05D23" }} /> Bulk CTA Import
            <span className="text-xs font-normal text-slate-500 ml-2">Up to {MAX_FILES} files per batch</span>
          </DialogTitle>
        </DialogHeader>

        {step === "pick" && (
          <div className="py-6">
            <div
              className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:border-[#0A4C95] transition-colors cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              data-testid="bulk-drop-zone"
            >
              <UploadCloud className="w-10 h-10 mx-auto text-slate-400" />
              <div className="mt-3 text-sm font-semibold">Drop multiple CTA documents</div>
              <div className="text-xs text-slate-500 mt-1">PDF · DOCX · XLSX · TXT — selected together (Cmd/Ctrl+click)</div>
              <Button className="mt-4 bg-[#0A4C95] hover:bg-[#083d79]" data-testid="bulk-pick-btn">Choose Files</Button>
              <input
                ref={inputRef}
                type="file"
                hidden
                multiple
                accept=".pdf,.docx,.xlsx,.xlsm,.txt"
                onChange={onPick}
                data-testid="bulk-file-input"
              />
            </div>
            <div className="mt-5 text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3">
              <strong>How it works:</strong> Each file is sent to Claude Sonnet 4.5 in parallel (4 at a time).
              You'll review every extraction, assign a site, and confirm before anything saves. Typical throughput:
              ~10–20 seconds per CTA.
            </div>
          </div>
        )}

        {step === "extracting" && (
          <div className="py-12 text-center" data-testid="bulk-progress">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#0A4C95]" />
            <div className="mt-4 text-lg font-semibold">
              Extracting {job?.completed ?? 0} / {job?.total ?? "…"}
            </div>
            <div className="text-sm text-slate-500 mt-1">Claude is parsing your CTAs in parallel</div>
            {job?.total > 0 && (
              <div className="max-w-md mx-auto mt-5">
                <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-[#0A4C95] transition-all duration-500"
                    style={{ width: `${Math.round(((job.completed || 0) / job.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            <div className="mt-6 max-w-2xl mx-auto text-left">
              {(job?.results || []).map((r) => (
                <div key={r.index} className="flex items-center gap-2 text-xs py-1">
                  {r.status === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                  {r.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0A4C95] shrink-0" />}
                  {r.status === "pending" && <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                  {r.status === "error" && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                  <span className="truncate">{r.filename}</span>
                  {r.status === "error" && <span className="text-red-600 ml-2 truncate">{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(step === "review" || step === "saving" || step === "done") && (
          <div className="py-2 space-y-3" data-testid="bulk-review">
            {step !== "done" && (
              <div className="flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded p-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={autoAssignBySponsor}
                  className="bg-white"
                  data-testid="bulk-auto-assign-btn"
                  title="Match each CTA's sponsor against your existing CTAs and assign the most-frequent site for that sponsor"
                >
                  <Wand2 className="w-3.5 h-3.5 mr-1" style={{ color: "#F05D23" }} />
                  Auto-assign by sponsor
                </Button>
                <span className="text-xs text-slate-400">·</span>
                <span className="text-xs font-semibold text-slate-700">Apply site to all:</span>
                <Select value={bulkSite} onValueChange={setBulkSite}>
                  <SelectTrigger className="w-64 h-8 bg-white" data-testid="bulk-site-select"><SelectValue placeholder="Pick a site..." /></SelectTrigger>
                  <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={applyBulkSite} disabled={!bulkSite} data-testid="bulk-apply-site-btn">Apply to all</Button>
                <div className="ml-auto text-xs text-slate-600">
                  {includedRows.length} selected · {rows.filter((r) => r.error).length} failed
                </div>
              </div>
            )}

            {step === "done" && saveResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm" data-testid="bulk-save-summary">
                <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-700" />
                <strong>{saveResult.total_created}</strong> CTA(s) saved successfully
                {saveResult.total_errors > 0 && <>, <strong className="text-red-700">{saveResult.total_errors}</strong> failed</>}.
              </div>
            )}

            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs stryde-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-2 py-2 text-left w-8"></th>
                    <th className="px-2 py-2 text-left">File</th>
                    <th className="px-2 py-2 text-left">Study</th>
                    <th className="px-2 py-2 text-left">Sponsor</th>
                    <th className="px-2 py-2 text-right">Budget</th>
                    <th className="px-2 py-2 text-center">Counts</th>
                    <th className="px-2 py-2 text-center">Coverage</th>
                    <th className="px-2 py-2 text-left w-56">Site *</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-500">No rows.</td></tr>}
                  {rows.map((r, i) => {
                    if (r.error) {
                      return (
                        <tr key={r._k} className="border-t border-slate-100 bg-red-50/40" data-testid={`bulk-row-${i}`}>
                          <td className="px-2 py-2"><AlertTriangle className="w-4 h-4 text-red-500" /></td>
                          <td className="px-2 py-2 text-slate-700 truncate max-w-[200px]" title={r.filename}>{r.filename}</td>
                          <td colSpan={6} className="px-2 py-2 text-red-700 truncate" title={r.error}>{r.error}</td>
                          <td className="px-2 py-2"><Button size="icon" variant="ghost" onClick={() => removeRow(i)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      );
                    }
                    const d = r.data || {};
                    const cov = r.coverage;
                    const counts = d._counts || {};
                    return (
                      <tr key={r._k} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bulk-row-${i}`}>
                        <td className="px-2 py-2">
                          <Checkbox checked={r.include} onCheckedChange={(v) => updateRow(i, { include: !!v })} disabled={step !== "review"} data-testid={`bulk-include-${i}`} />
                        </td>
                        <td className="px-2 py-2 truncate max-w-[200px] text-slate-600" title={r.filename}>{r.filename}</td>
                        <td className="px-2 py-2 font-semibold text-slate-900 truncate max-w-[180px]" title={d.study_name}>{d.study_name || <span className="text-slate-400 italic">unknown</span>}</td>
                        <td className="px-2 py-2 text-slate-700 truncate max-w-[140px]">{d.sponsor || <span className="text-slate-400 italic">—</span>}</td>
                        <td className="px-2 py-2 text-right font-mono-fin">{fmtUSD(d.total_budget)}</td>
                        <td className="px-2 py-2 text-center text-[10.5px] text-slate-600">
                          {counts.visits || 0}v · {counts.activities || 0}a · {counts.invoicables || 0}i
                        </td>
                        <td className="px-2 py-2 text-center">
                          {cov && (
                            <Badge variant="outline" className={bandColor(cov.band)}>
                              <span className="font-mono-fin font-semibold">{cov.score}</span>
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <Select value={r.site_id} onValueChange={(v) => updateRow(i, { site_id: v })} disabled={step !== "review"}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`bulk-site-${i}`}><SelectValue placeholder="Pick site..." /></SelectTrigger>
                            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          {step === "review" && (
                            <Button size="icon" variant="ghost" onClick={() => removeRow(i)} title="Remove from batch"><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" /></Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {saveResult?.errors?.length > 0 && (
              <div className="border border-red-200 rounded p-3 bg-red-50 text-xs">
                <strong className="text-red-800">Save errors:</strong>
                <ul className="mt-1 space-y-0.5">
                  {saveResult.errors.map((e, i) => (
                    <li key={`${e.source_filename || "row"}-${i}`} className="text-red-700">• {e.source_filename}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "review" && (
            <>
              <Button variant="ghost" onClick={reset}>Start Over</Button>
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button
                onClick={saveAll}
                disabled={includedRows.length === 0 || !allHaveSite}
                className="bg-[#0A4C95] hover:bg-[#083d79]"
                data-testid="bulk-save-all-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                Save {includedRows.length} CTA{includedRows.length !== 1 ? "s" : ""}
              </Button>
            </>
          )}
          {step === "saving" && (
            <Button disabled className="bg-[#0A4C95]"><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</Button>
          )}
          {step === "done" && (
            <Button onClick={() => { setOpen(false); reset(); }} className="bg-[#0A4C95] hover:bg-[#083d79]" data-testid="bulk-close-btn">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
